import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link, useParams } from "react-router-dom";
import { Container } from "@/components/Container";
import { Persona, usePersonaWithDocumentsQuery } from "@/api/personas.ts";
import { useToast } from "@/hooks/use-toast.ts";
import { ChevronLeftIcon, Coins, LoaderCircle, Settings } from "lucide-react";
import { Role } from "@/components/AuthorizationProvider";
import TokenRow from "@/components/TokenRow";
import PersonaDetailsHeader from "@/components/PersonaDetailsHeader";
import { usePrivy } from "@privy-io/react-auth";
import { AgentEvolution } from "@/components/manage-personas/AgentEvolution";
import { persistLastPersonaId } from "@/hooks/use-selected-persona";
import PersonaChatFixed from "@/components/PersonaChatFixed";
import { useAuth } from "@/hooks/use-auth.tsx";
import { agentQueries } from "@/api/api";
import { useQuery } from "@tanstack/react-query";

export function PersonaView() {
  const { toast } = useToast();
  const { personaId } = useParams();
  const { authenticated } = usePrivy();

  useEffect(() => {
    if (personaId) {
      persistLastPersonaId(personaId);
    }
  }, [personaId]);

  const personaWithDocumentsQuery = usePersonaWithDocumentsQuery(
    personaId ?? "",
    authenticated,
  );

  // @ts-expect-error this defaulting is invalid
  const personaData: Persona = personaWithDocumentsQuery.data?.persona || {
    persona_id: "",
    name: "",
    gender: "male",
  };

  const docData = personaWithDocumentsQuery.data?.documents || {};

  const { hasAtLeastRole } = useAuth();

  const { data } = useQuery(agentQueries.agent(personaData.persona_id));
  const canEdit = data?.can_edit_persona;

  useEffect(() => {
    if (personaWithDocumentsQuery.isError) {
      toast({ description: "Error loading agent or documents" });
    }
  }, [personaWithDocumentsQuery.isError]);

  if (personaWithDocumentsQuery.isLoading) {
    return (
      <Container>
        <div className="flex items-center justify-center h-screen">
          <LoaderCircle className="h-12 w-12 animate-spin text-primary" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="mx-auto w-full container-fluid">
      {/* Top Navigation */}
      <div className="relative w-full flex flex-row justify-between items-center mb-4">
        <Link className="" to={authenticated ? "/agents" : "/"}>
          <Button
            className="hidden sm:flex flex-row gap-x-2 text-primary"
            variant="ghost"
          >
            <ChevronLeftIcon /> Back
          </Button>
        </Link>

        <div className="flex flex-wrap flex-row gap-2 justify-end">
          <>
            {(canEdit || personaData.public_persona) && (
              <PersonaChatFixed
                chatUrl={`${import.meta.env.VITE_CHAT_URL}?persona_id=${personaId}`}
                personaData={personaData}
              />
            )}
            {canEdit && (
              <Button variant="outline" className="text-accent" asChild>
                <Link
                  className="inline-flex items-center"
                  to={`/agents/${personaId}/edit`}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Agent settings
                </Link>
              </Button>
            )}
          </>
          {canEdit &&
            hasAtLeastRole(Role.SuperAdmin) &&
            !personaData.web3?.metadata?.symbol && (
              <Button asChild>
                <Link
                  className="inline-flex items-center"
                  to={`/agents/${personaId}/token`}
                >
                  <Coins className="w-4 h-4 mr-2" />
                  Mint Token
                </Link>
              </Button>
            )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-col gap-4">
        <PersonaDetailsHeader
          personaId={personaId!}
          personaData={personaData}
        />
        {personaData.web3?.contractAddress && (
          <div className="w-full flex justify-between  lg:flex-row flex-col gap-4">
            <div className="w-full border bg-darkGray rounded-2xl backdrop-blur-xl p-1">
              <TokenRow
                tokenStage={personaData.web3?.tokenStage}
                blockchain={personaData.web3?.blockchain}
                contractAddress={personaData.web3?.contractAddress}
                bondingCurveData={personaData.web3?.bondingCurve}
                raydiumPoolAddress={personaData.web3?.raydium?.poolAddress}
                token={{
                  name: personaData.web3?.metadata?.name as string,
                  symbol: personaData.web3?.metadata?.symbol as string,
                  persona_id: personaData.persona_id,
                }}
              />
            </div>
          </div>
        )}

        <div className="flex flex-col flex-grow w-full border bg-darkGray rounded-2xl p-6">
          <AgentEvolution personaData={personaData} docData={docData} />
        </div>
      </div>
    </Container>
  );
}
